import React, { useState } from 'react';
import { donationTiers } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Heart, CreditCard, Wallet, Mail, User, Phone } from 'lucide-react';
import { Badge } from './ui/badge';

export const GetInvolved = () => {
  const [selectedTier, setSelectedTier] = useState(donationTiers[1]);
  const [customAmount, setCustomAmount] = useState('');
  const [donationType, setDonationType] = useState('one-time');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleDonation = (e) => {
    e.preventDefault();
    const amount = selectedTier.amount === 'custom' ? customAmount : selectedTier.amount;
    toast.success(`Thank you for your ${donationType} donation of $${amount}! Processing...`);
  };

  const handleVolunteer = (e) => {
    e.preventDefault();
    toast.success('Thank you for your interest! We will contact you soon.');
  };

  return (
    <section id="donate" className="py-24 bg-gradient-to-b from-gray-900 to-amber-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 text-white">
          <h2 className="font-playfair text-5xl md:text-6xl font-bold mb-6">
            Get Involved
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your generosity transforms lives. Choose how you'd like to create hope.
          </p>
        </div>

        <Tabs defaultValue="donate" className="max-w-5xl mx-auto">
          <TabsList className="grid w-full grid-cols-3 mb-12 bg-white/10 backdrop-blur-md p-2">
            <TabsTrigger value="donate" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-white">
              <Heart className="h-4 w-4 mr-2" />
              Donate
            </TabsTrigger>
            <TabsTrigger value="volunteer" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-white">
              Volunteer
            </TabsTrigger>
            <TabsTrigger value="partner" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-white">
              Corporate Partnership
            </TabsTrigger>
          </TabsList>

          {/* Donate Tab */}
          <TabsContent value="donate">
            <Card className="border-none shadow-2xl">
              <CardHeader>
                <CardTitle className="font-playfair text-3xl">Make a Donation</CardTitle>
                <CardDescription className="text-base">
                  Choose your contribution level and payment method
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Donation Tiers */}
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                  {donationTiers.map((tier) => (
                    <button
                      key={tier.id}
                      onClick={() => setSelectedTier(tier)}
                      className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                        selectedTier.id === tier.id
                          ? 'border-amber-600 bg-amber-50 shadow-lg transform scale-105'
                          : 'border-gray-200 hover:border-amber-400 hover:shadow-md'
                      }`}
                    >
                      {tier.popular && (
                        <Badge className="absolute -top-3 right-4 bg-amber-600 text-white">
                          Popular
                        </Badge>
                      )}
                      <h3 className="font-bold text-lg mb-2">{tier.name}</h3>
                      <p className="text-3xl font-bold text-amber-600 mb-2">
                        {tier.amount === 'custom' ? '—' : `$${tier.amount}`}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">{tier.description}</p>
                      <p className="text-xs text-amber-700 font-medium">{tier.impact}</p>
                    </button>
                  ))}
                </div>

                {/* Custom Amount Input */}
                {selectedTier.amount === 'custom' && (
                  <div className="mb-8">
                    <Label htmlFor="custom-amount">Enter Your Amount (USD)</Label>
                    <Input
                      id="custom-amount"
                      type="number"
                      placeholder="100"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="text-lg"
                    />
                  </div>
                )}

                {/* Donation Type */}
                <div className="mb-8">
                  <Label className="mb-3 block">Donation Frequency</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setDonationType('one-time')}
                      className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                        donationType === 'one-time'
                          ? 'border-amber-600 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-400'
                      }`}
                    >
                      <p className="font-semibold">One-Time</p>
                      <p className="text-sm text-gray-600">Single contribution</p>
                    </button>
                    <button
                      onClick={() => setDonationType('monthly')}
                      className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                        donationType === 'monthly'
                          ? 'border-amber-600 bg-amber-50'
                          : 'border-gray-200 hover:border-amber-400'
                      }`}
                    >
                      <p className="font-semibold">Monthly</p>
                      <p className="text-sm text-gray-600">Recurring support</p>
                    </button>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mb-8">
                  <Label className="mb-3 block">Payment Method</Label>
                  <Tabs defaultValue="stripe" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="stripe">
                        <CreditCard className="h-4 w-4 mr-2" />
                        Credit Card (Stripe)
                      </TabsTrigger>
                      <TabsTrigger value="crypto">
                        <Wallet className="h-4 w-4 mr-2" />
                        Cryptocurrency
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="stripe" className="space-y-4 mt-6">
                      <div>
                        <Label htmlFor="card-number">Card Number</Label>
                        <Input id="card-number" placeholder="4242 4242 4242 4242" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expiry">Expiry Date</Label>
                          <Input id="expiry" placeholder="MM/YY" />
                        </div>
                        <div>
                          <Label htmlFor="cvc">CVC</Label>
                          <Input id="cvc" placeholder="123" />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="crypto" className="mt-6">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                        <Wallet className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                        <h3 className="font-bold text-lg mb-2">Cryptocurrency Donation</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          Choose your preferred cryptocurrency to donate
                        </p>
                        <div className="space-y-2">
                          <Button variant="outline" className="w-full">Bitcoin (BTC)</Button>
                          <Button variant="outline" className="w-full">Ethereum (ETH)</Button>
                          <Button variant="outline" className="w-full">3DOT Token</Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                          All crypto transactions are verified on blockchain
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Donor Information */}
                <div className="space-y-4 mb-8">
                  <div>
                    <Label htmlFor="donor-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="donor-name"
                        placeholder="John Doe"
                        className="pl-10"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="donor-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        id="donor-email"
                        type="email"
                        placeholder="john@example.com"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleDonation}
                  size="lg"
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  <Heart className="mr-2 h-5 w-5" />
                  Complete Donation
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Volunteer Tab */}
          <TabsContent value="volunteer">
            <Card className="border-none shadow-2xl">
              <CardHeader>
                <CardTitle className="font-playfair text-3xl">Become a Volunteer</CardTitle>
                <CardDescription className="text-base">
                  Share your time and skills to make a direct impact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVolunteer} className="space-y-6">
                  <div>
                    <Label htmlFor="vol-name">Full Name</Label>
                    <Input id="vol-name" placeholder="Your name" required />
                  </div>
                  <div>
                    <Label htmlFor="vol-email">Email Address</Label>
                    <Input id="vol-email" type="email" placeholder="your@email.com" required />
                  </div>
                  <div>
                    <Label htmlFor="vol-phone">Phone Number</Label>
                    <Input id="vol-phone" placeholder="+63 XXX XXX XXXX" />
                  </div>
                  <div>
                    <Label htmlFor="vol-interest">Areas of Interest</Label>
                    <textarea
                      id="vol-interest"
                      className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Tell us about your skills and how you'd like to help..."
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partner Tab */}
          <TabsContent value="partner">
            <Card className="border-none shadow-2xl">
              <CardHeader>
                <CardTitle className="font-playfair text-3xl">Corporate Partnership</CardTitle>
                <CardDescription className="text-base">
                  Partner with Kibouor through AR Holdings Group CSR Initiative
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    Join leading organizations in supporting Kibouor's mission. Corporate partnerships
                    provide sustainable funding, employee engagement opportunities, and positive social impact.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <h4 className="font-bold text-lg mb-3">Partnership Benefits:</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>Brand association with humanitarian excellence</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>Employee volunteer programs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>Tax-deductible contributions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-600 mt-1">•</span>
                        <span>Blockchain-verified impact reporting</span>
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-4">
                    <Input placeholder="Company Name" />
                    <Input placeholder="Contact Person" />
                    <Input type="email" placeholder="Business Email" />
                    <Input placeholder="Phone Number" />
                    <textarea
                      className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Tell us about your organization and partnership interests..."
                    />
                  </div>
                  <Button
                    size="lg"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
                  >
                    Request Partnership Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};